import type { ComponentContentSchema } from '@vibesites/runtime';
import { EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { HeroSolidBGView, type HeroSolidBGContentProps } from './HeroSolidBG';

export function ContactHero(props: HeroSolidBGContentProps) {
  const bgDefinition = contentSchema.slotGroups!['contacthero.bg_image'];
  const ctaDefinition = contentSchema.slotGroups!['contacthero.cta_link'];
  return (
    <>
      <HeroSolidBGView {...props} prefix="contacthero" bgDefinition={bgDefinition} />

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent path="contacthero.title" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableToken path="contacthero.subtitle" type="richtext" label="Subtitle" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent path="contacthero.blurb" defaultValue="" as="span" className="hidden" style={{ display: 'none' }} />
      <EditableSlotGroup groupPath="contacthero.bg_image" definition={bgDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="contacthero.cta_link" definition={ctaDefinition} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactHero',
  displayName: 'Contact Hero',
  tokens: {
    'contacthero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Contact Us</p>',
    },
    'contacthero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Contact <span data-type="field-token" data-field="property_name"></span> in <span data-type="field-token" data-field="property_address_city"></span>, <span data-type="field-token" data-field="property_address_state"></span></p>',
    },
    'contacthero.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultField: 'property_contactus_blurb',
    },
  },
  slotGroups: {
    'contacthero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Property exterior',
        },
      },
      generator: {
        prompt:
          'Pick the hero background photo for the Contact Us page: a strong photo of this property exterior, entrance, or community. Write alt text describing what the photo shows.',
        context:
          'Full-bleed background hero at the top of the Contact Us page, behind a semi-opaque primary-color overlay with centered white headline text. Prefer staged or professional photos; skip blurry, dark, cluttered, or unappealing shots. Priority: relevance to the property exterior or entrance first, image quality second, uniqueness third. Each inner page (Floor Plans, Amenities, Gallery, Virtual Tour, Neighborhood, Contact Us) has its own hero — do not reuse an image already used as another page hero or the homepage hero unless no other suitable image exists.',
      },
    },
    'contacthero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(ContactHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactHero;
